import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Typography } from '@/components';
import { Dialog, DialogContent, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from '@/components/dialog';
import { useCountries, useDevelopmentStatus, useTechnologies } from '@/hooks';

import { useUpdateCompanyMutation } from '../../api';
import { useNotifyOnboardingCompleteMakeMutation } from '@/features/users/api/integrations';
import { CompanySchema } from '../../types';
import { RolesSection } from './roles-section';
import { TechnologiesSection } from './technologies-section';
import { CountriesSection } from './countries-section';
import { DevelopmentStagesSection } from './development-stages-section';
import { SizeSection } from './size-section';
import { useForm } from 'react-hook-form';
import { InterestModalStepper } from './stepper';

import {
  SectionErrors,
  validateRoles,
  validateTechnologies,
  validateCountries,
  validateSolarSize,
  validateSolarStages,
  validateBessSize,
  validateBessStages,
} from '../../helpers/preferences-validation';
import { ErrorMessage } from './error-message';

export type PreferencesModalProps = {
  readonly company?: CompanySchema;
  readonly open?: boolean;
  readonly setOpen?: (state: boolean) => void;
};

export type PreferencesFormFields = {
  activity_interests: string[];
  technology_interests: string[];
  other_technology: string;
  countries_interests: string[];
  location_interests: string;
  modal_interests: boolean;
  bess_size: [string, string];
  development_status_interests: string[];
  solar_size: [string, string];
  solar_development_status: string[];
};

export function PreferencesModal({ company, open = false, setOpen }: PreferencesModalProps) {
  const { t } = useTranslation(['settings']);

  const [setPreferences] = useUpdateCompanyMutation();
  const [notifyOnboardingCompleteMake] = useNotifyOnboardingCompleteMakeMutation();
  const [disabled, setDisabled] = useState(false);

  const submittingRef = useRef(false);

  const handleOnSubmit = useCallback(
    ({ location_interests, ...data }: PreferencesFormFields) => {
      if (!company) return;
      submittingRef.current = true;
      setDisabled(true);

      setPreferences({
        id: company.id,
        data: { ...data, location_interests: location_interests.split(','), modal_interests: true },
      })
        .unwrap()
        .then(() => {
          notifyOnboardingCompleteMake()
            .unwrap()
            .catch((error) => {
              console.error('Failed to notify Make:', error);
            });
        })
        .finally(() => setOpen?.(false));
    },
    [company, setOpen, setPreferences, notifyOnboardingCompleteMake]
  );

  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const options = useMemo(
    () => ({
      defaultValues: {
        activity_interests: company?.activity_interests ?? [],
        technology_interests: company?.technology_interests ?? [],
        other_technology: company?.other_technology ?? '',
        countries_interests: company?.countries_interests ?? [],
        location_interests: company?.location_interests ?? '',
        modal_interests: false,
        bess_size: company?.bess_size ?? [],
        development_status_interests: company?.development_status_interests ?? [],
        solar_size: company?.solar_size ?? [],
        solar_development_status: company?.solar_development_status ?? [],
      },
    }),
    [company]
  );

  const roles = [
    { value: 'buyer', label: t('Investor') },
    { value: 'seller', label: t('Seller / Developer') },
    { value: 'service-provider', label: t('Service Provider') },
    { value: 'debt-provider', label: t('Debt Provider') },
  ];
  const { filters: technologies } = useTechnologies();
  const { options: countries } = useCountries();

  const [currentStep, setCurrentStep] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [sectionErrors, setSectionErrors] = useState<SectionErrors>({});
  const clearSectionError = (section: keyof SectionErrors) => {
    setSectionErrors((prev) => ({ ...prev, [section]: undefined }));
  };

  const methods = useForm<PreferencesFormFields>(options);
  const { watch, setValue, register, handleSubmit } = methods;
  const formValues = watch();

  const getSteps = () => {
    const steps = ['initial'];
    const isInvestor = formValues.activity_interests?.includes('buyer') ?? false;

    if (isInvestor && formValues.technology_interests?.includes('solar')) {
      steps.push('solar');
    }
    if (isInvestor && formValues.technology_interests?.includes('bess')) {
      steps.push('bess');
    }

    steps.push('thankYou');

    return steps;
  };

  const validateCurrentStep = (): boolean => {
    setValidationError('');

    if (currentStep === 0) {
      const rolesResult = validateRoles(formValues);
      const techResult = validateTechnologies(formValues);
      const countriesResult = validateCountries(formValues);

      setSectionErrors({
        roles: rolesResult.error,
        technologies: techResult.error,
        countries: countriesResult.error,
      });

      return rolesResult.isValid && techResult.isValid && countriesResult.isValid;
    }

    if (currentStepType === 'solar') {
      const sizeResult = validateSolarSize(formValues);
      const stagesResult = validateSolarStages(formValues);

      setSectionErrors({
        solarSize: sizeResult.error,
        solarStages: stagesResult.error,
      });

      return sizeResult.isValid && stagesResult.isValid;
    }

    if (currentStepType === 'bess') {
      const sizeResult = validateBessSize(formValues);
      const stagesResult = validateBessStages(formValues);

      setSectionErrors({
        bessSize: sizeResult.error,
        bessStages: stagesResult.error,
      });

      return sizeResult.isValid && stagesResult.isValid;
    }

    return true;
  };

  const handleNext = () => {
    const steps = getSteps();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      if (submittingRef.current) return;
      handleSubmit(handleOnSubmit)();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = getSteps();
  const currentStepType = steps[currentStep];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent onPointerDownOutside={(event) => event.preventDefault()} canClose={false} className="p-10">
          <DialogHeader className="mb-2 flex-row items-center gap-3">
            {currentStepType === 'initial' && (
              <>
                <Icon name="person" className="text-primary-700 h-8 w-8" />
                <DialogTitle className="font-bold">{t('Tell us a bit more about you...')}</DialogTitle>
              </>
            )}
            {currentStepType === 'solar' && (
              <>
                <Icon name="solar" className="text-primary-700 h-8 w-8" />
                <DialogTitle>{t('Tell us more about your interest in Solar PV')}</DialogTitle>
              </>
            )}
            {currentStepType === 'bess' && (
              <>
                <Icon name="solar" className="text-primary-700 h-8 w-8" />
                <DialogTitle>{t('Tell us more about your interest in BESS')}</DialogTitle>
              </>
            )}
            {currentStepType === 'thankYou' && <DialogTitle className="font-bold">{t('Thank you!')}</DialogTitle>}
          </DialogHeader>

          <form id="preferences-form" onSubmit={handleSubmit(handleOnSubmit)}>
            <div className="space-y-6">
              {currentStepType === 'initial' && (
                <>
                  <RolesSection
                    roles={roles}
                    registration={register('activity_interests', {
                      onChange: () => clearSectionError('roles'),
                    })}
                    selectedRoles={watch('activity_interests')}
                  />
                  <ErrorMessage error={sectionErrors.roles} />
                  <TechnologiesSection
                    technologies={technologies}
                    registration={register('technology_interests', {
                      onChange: () => clearSectionError('technologies'),
                    })}
                    selectedTechnologies={watch('technology_interests')}
                    otherValue={watch('other_technology')}
                    onOtherChange={(value) => {
                      setValue('other_technology', value);
                    }}
                  />
                  <ErrorMessage error={sectionErrors.technologies} />
                  <CountriesSection
                    countries={countries}
                    registration={register('countries_interests', {
                      onChange: () => clearSectionError('countries'),
                    })}
                    selectedCountries={watch('countries_interests')}
                    otherValue={watch('location_interests')}
                    onOtherChange={(value) => {
                      setValue('location_interests', value);
                    }}
                  />
                  <ErrorMessage error={sectionErrors.countries} />
                </>
              )}
              {currentStepType === 'bess' && (
                <>
                  <SizeSection
                    value={watch('bess_size')}
                    onChange={(value) => {
                      setValue('bess_size', value);
                      clearSectionError('bessSize');
                    }}
                    unit="MW"
                    fromRegistration={register('bess_size.0')}
                    toRegistration={register('bess_size.1')}
                  />
                  <ErrorMessage error={sectionErrors.bessSize} />
                  <DevelopmentStagesSection
                    stages={developmentStatusOptions}
                    registration={register('development_status_interests', {
                      onChange: () => clearSectionError('bessStages'),
                    })}
                    selectedStages={watch('development_status_interests')}
                  />
                  <ErrorMessage error={sectionErrors.bessStages} />
                </>
              )}
              {currentStepType === 'solar' && (
                <>
                  <SizeSection
                    value={watch('solar_size')}
                    onChange={(value) => {
                      setValue('solar_size', value);
                      clearSectionError('solarSize');
                    }}
                    unit="MWp"
                    fromRegistration={register('solar_size.0')}
                    toRegistration={register('solar_size.1')}
                  />
                  <ErrorMessage error={sectionErrors.solarSize} />
                  <DevelopmentStagesSection
                    stages={developmentStatusOptions}
                    registration={register('solar_development_status', {
                      onChange: () => clearSectionError('solarStages'),
                    })}
                    selectedStages={watch('solar_development_status')}
                  />
                  <ErrorMessage error={sectionErrors.solarStages} />
                </>
              )}
              {currentStepType === 'thankYou' && (
                <div className="flex min-h-[240px] flex-col items-center justify-center space-y-6 text-center">
                  <div className="rounded-full bg-white/50 p-4 backdrop-blur-sm">
                    <Icon name="circle-check" className="text-primary-700 h-16 w-16" />
                  </div>
                  <Typography as="p" color="primary" size="title" weight="semibold">
                    {t('We will keep you updated according to your preferences')}
                  </Typography>
                </div>
              )}
            </div>
          </form>

          <InterestModalStepper
            onNext={handleNext}
            onBack={handleBack}
            validationError={validationError}
            isLastStep={currentStep === steps.length - 1}
            isThankYouStep={currentStepType === 'thankYou'}
            showBackButton={currentStep > 0}
            validateCurrentStep={validateCurrentStep}
            disabled={disabled}
          />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
